import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaBook,
  FaBookOpen,
  FaCalendarAlt,
  FaChalkboardTeacher,
  FaEdit,
  FaExchangeAlt,
  FaHome,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrashAlt,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import MdlEditSubjectMasterList from "../dialog/MdlEditSubjectMasterList";
import MdlAddSubjectMasterList from "../dialog/MdlAddSubjectMasterList";
import MdlSelectSession from "../dialog/MdlSelectSession";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor — rotating accents keep long lists readable
const CARD_TONES = ["sky", "teal", "indigo", "amber", "rose", "violet"];

//Created By Cursor
function subjectInitials(name) {
  const words = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "S";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

//Created By Cursor
const SubjectCard = memo(function SubjectCard({
  item,
  index,
  busy,
  onEdit,
  onDelete,
}) {
  const [showAllUsage, setShowAllUsage] = useState(false);
  const tone = CARD_TONES[index % CARD_TONES.length];
  const usage = Array.isArray(item.class_usage) ? item.class_usage : [];
  const usageCount = Number(item.class_usage_count || usage.length || 0);
  const canDelete = usageCount === 0;
  const previewLimit = 6;
  const visibleUsage = showAllUsage ? usage : usage.slice(0, previewLimit);
  const more = Math.max(0, usageCount - previewLimit);

  return (
    <article
      className={`sl-card sl-card--${tone}`}
      style={{ "--sl-i": index }}
    >
      <div className="sl-card__top">
        <div className="sl-card__avatar" aria-hidden>
          <span>{subjectInitials(item.subject_name)}</span>
        </div>
        <div className="sl-card__body min-w-0">
          <h3 className="sl-card__name" title={item.subject_name || ""}>
            {item.subject_name || "—"}
          </h3>
          <div className="sl-card__meta-row">
            {item.subject_id ? (
              <span className="sl-card__id">
                <FaBook aria-hidden /> {item.subject_id}
              </span>
            ) : null}
            <span
              className={`sl-card__usage-count${
                usageCount > 0 ? " is-used" : ""
              }`}
            >
              <FaChalkboardTeacher aria-hidden />
              {usageCount > 0
                ? `In ${usageCount} class${usageCount === 1 ? "" : "es"}`
                : "Not used in class"}
            </span>
          </div>
        </div>
        <div className="sl-card__actions">
          <button
            type="button"
            className="sl-icon-btn"
            title="Edit subject"
            aria-label={`Edit ${item.subject_name || "subject"}`}
            disabled={busy}
            onClick={() => onEdit(item)}
          >
            <FaEdit />
          </button>
          {canDelete ? (
            <button
              type="button"
              className="sl-icon-btn sl-icon-btn--danger"
              title="Delete subject"
              aria-label={`Delete ${item.subject_name || "subject"}`}
              disabled={busy}
              onClick={() => onDelete(item)}
            >
              <FaTrashAlt />
            </button>
          ) : null}
        </div>
      </div>

      <div className="sl-card__usage" aria-label="Class subject usage">
        {usageCount === 0 ? (
          <p className="sl-card__usage-empty">
            Not linked to any class subject in this session
          </p>
        ) : (
          <div className="sl-card__usage-chips">
            {visibleUsage.map((u, i) => (
              <span
                key={`${u.class_id}-${u.section_id}-${i}`}
                className={`sl-usage-chip${
                  String(u.subject_type || "").toUpperCase() === "ALTERNATIVE"
                    ? " is-alt"
                    : ""
                }`}
                title={
                  u.subject_type
                    ? `${u.label} (${u.subject_type})`
                    : u.label || ""
                }
              >
                {u.label || "Class"}
              </span>
            ))}
            {more > 0 ? (
              <button
                type="button"
                className="sl-usage-chip sl-usage-chip--more"
                onClick={() => setShowAllUsage((v) => !v)}
                aria-expanded={showAllUsage}
                title={showAllUsage ? "Show less" : "Show all classes"}
              >
                {showAllUsage ? "Show less" : `+${more} more`}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
});

//Created By Cursor
export default function SubjectList() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [subjectList, setSubjectList] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState({
    name: "",
    id: "",
  });
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);

  //Created By Cursor — boot current session
  useEffect(() => {
    window.scrollTo(0, 0);
    const mSession = SessionManager.getCurrentSession();
    if (mSession && mSession[0]) {
      setSessionId(mSession[0].session_id || "");
      setSessionName(mSession[0].session_name || "");
      setSessionStatus(mSession[0].status || "");
    } else {
      setLoading(false);
      myToast.notify_warning("No active session found.");
    }
  }, []);

  //Created By Cursor
  const loadSubjects = useCallback(
    async ({ soft = false } = {}) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !sessionId) {
        setSubjectList([]);
        setLoading(false);
        return;
      }
      if (soft) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await axios.get(`${API_BASE()}getSubjectMasterList.php`, {
          params: { school_code: schoolCode, session_id: sessionId },
        });
        if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
          setSubjectList(res.data.data);
          if (res.data.initiated) {
            const n = Number(res.data.initiated_subject_count || 0);
            const sc = Array.isArray(res.data.initiated_sessions)
              ? res.data.initiated_sessions.length
              : 0;
            myToast.notify_success(
              sc > 1
                ? `Subject master initiated for ${sc} sessions (${n} subjects).`
                : `Subject master initiated for this session (${n} subjects).`
            );
          }
        } else {
          setSubjectList([]);
          if (res.data?.api_remark) {
            myToast.notify_warning(res.data.api_remark);
          }
        }
      } catch (err) {
        console.error("[SubjectList]", err);
        setSubjectList([]);
        myToast.notify_warning("Failed to load subjects.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (sessionId) {
      loadSubjects();
    }
  }, [sessionId, loadSubjects]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return subjectList;
    return subjectList.filter((s) => {
      const usageLabels = Array.isArray(s.class_usage)
        ? s.class_usage.map((u) => u.label || "").join(" ")
        : "";
      const hay = `${s.subject_name || ""} ${s.subject_id || ""} ${usageLabels}`.toLowerCase();
      return hay.includes(q);
    });
  }, [subjectList, searchText]);

  const usedInClassCount = useMemo(
    () =>
      subjectList.filter((s) => Number(s.class_usage_count || 0) > 0).length,
    [subjectList]
  );

  //Created By Cursor
  const openEdit = useCallback((item) => {
    setSelectedSubject({
      name: item.subject_name || "",
      id: item.subject_id || "",
    });
    setEditOpen(true);
  }, []);

  //Created By Cursor
  const closeEdit = useCallback(() => {
    setEditOpen(false);
    setSelectedSubject({ name: "", id: "" });
    loadSubjects({ soft: true });
  }, [loadSubjects]);

  //Created By Cursor
  const closeAdd = useCallback(() => {
    setAddOpen(false);
    loadSubjects({ soft: true });
  }, [loadSubjects]);

  //Created By Cursor — delete only when unused in class subjects for this session
  const handleDelete = useCallback(
    async (item) => {
      const subjectId = item?.subject_id || "";
      const subjectName = item?.subject_name || "this subject";
      if (!subjectId || !sessionId) {
        myToast.notify_warning("Subject or session missing.");
        return;
      }
      if (Number(item?.class_usage_count || 0) > 0) {
        myToast.notify_warning("Cannot delete. Subject is used in class.");
        return;
      }
      const ok = window.confirm(
        `Delete "${subjectName}" from this session?\nThis only removes unused master subjects.`
      );
      if (!ok) return;

      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id || "";
      if (!schoolCode) {
        myToast.notify_warning("School code missing.");
        return;
      }

      setDeleting(true);
      try {
        const params = new URLSearchParams();
        params.append("school_code", schoolCode);
        params.append("session_id", sessionId);
        params.append("subject_id", subjectId);
        if (staffId) params.append("staff_id", staffId);
        const res = await axios.post(
          `${API_BASE()}deleteMasterSubject.php`,
          params
        );
        if (res.data?.api_status === "OK") {
          myToast.notify_success(res.data.api_remark || "Subject deleted.");
          await loadSubjects({ soft: true });
        } else {
          myToast.notify_warning(
            res.data?.api_remark || "Failed to delete subject."
          );
        }
      } catch (err) {
        console.error("[SubjectList] delete", err);
        myToast.notify_error("Failed to delete subject.");
      } finally {
        setDeleting(false);
      }
    },
    [sessionId, loadSubjects]
  );

  //Created By Cursor
  const handleSelectedSession = useCallback(
    (sid, sname, _start, _end, status) => {
      setIsOpenSessionDialog(false);
      setSessionId(sid || "");
      setSessionName(sname || "");
      setSessionStatus(status || "");
      setSearchText("");
    },
    []
  );

  const busy = loading || refreshing || deleting;
  const sessionActive = useMemo(() => {
    const s = String(sessionStatus || "").toLowerCase();
    return s === "active" || s === "current" || s === "1";
  }, [sessionStatus]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page sl-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Subject List</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell sl-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBookOpen />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Subject List</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · session-wise master subjects`
                  : "Session-wise master subjects for classes, exams & staff"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="sl-session-btn"
              onClick={() => setIsOpenSessionDialog(true)}
              disabled={busy}
              title="Change session"
            >
              <span className="sl-session-btn__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <span className="sl-session-btn__text">
                <small>Session</small>
                <strong>{sessionName || "Select session"}</strong>
              </span>
              {sessionStatus ? (
                <span
                  className={`sl-session-btn__status${
                    sessionActive ? " is-active" : ""
                  }`}
                >
                  {sessionStatus}
                </span>
              ) : (
                <span className="sl-session-btn__change" aria-hidden>
                  <FaExchangeAlt />
                </span>
              )}
            </button>
            <button
              type="button"
              className={`sl-hero-icon${refreshing ? " is-spinning" : ""}`}
              title="Refresh"
              disabled={busy || !sessionId}
              onClick={() => loadSubjects({ soft: true })}
            >
              <FaSyncAlt />
            </button>
            <button
              type="button"
              className="sl-btn sl-btn--hero"
              disabled={busy || !sessionId}
              onClick={() => setAddOpen(true)}
            >
              <FaPlus />
              <span>Add subject</span>
            </button>
          </div>
        </header>

        <div className="sl-body">
          <div className="sl-toolbar">
            <div className="sl-kpi">
              <FaBook aria-hidden />
              <strong>{subjectList.length}</strong>
              <span>subject{subjectList.length === 1 ? "" : "s"}</span>
            </div>
            <div className="sl-kpi sl-kpi--usage">
              <FaChalkboardTeacher aria-hidden />
              <strong>{usedInClassCount}</strong>
              <span>used in class</span>
            </div>
            <label className="sl-search">
              <FaSearch aria-hidden />
              <input
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search subject or class…"
                aria-label="Search subjects"
                disabled={!sessionId}
              />
            </label>
            <button
              type="button"
              className="sl-btn sl-btn--primary"
              disabled={busy || !sessionId}
              onClick={() => setAddOpen(true)}
            >
              <FaPlus />
              <span>Add</span>
            </button>
          </div>

          {!sessionId ? (
            <div className="sl-empty">
              <div className="sl-empty__icon" aria-hidden>
                <FaCalendarAlt />
              </div>
              <h3>Select a session</h3>
              <p>Subject master is managed session-wise.</p>
              <button
                type="button"
                className="sl-btn sl-btn--primary"
                onClick={() => setIsOpenSessionDialog(true)}
              >
                <FaCalendarAlt />
                <span>Choose session</span>
              </button>
            </div>
          ) : loading ? (
            <div className="sl-loading">
              <MyLoader />
            </div>
          ) : filtered.length === 0 ? (
            <div className="sl-empty">
              <div className="sl-empty__icon" aria-hidden>
                <FaBookOpen />
              </div>
              <h3>
                {subjectList.length === 0 ? "No subjects yet" : "No matches"}
              </h3>
              <p>
                {subjectList.length === 0
                  ? "Add subjects for this session, or they will auto-seed from the current master when a session is first opened."
                  : "Try a different search."}
              </p>
              {subjectList.length === 0 ? (
                <button
                  type="button"
                  className="sl-btn sl-btn--primary"
                  onClick={() => setAddOpen(true)}
                >
                  <FaPlus />
                  <span>Add first subject</span>
                </button>
              ) : null}
            </div>
          ) : (
            <div className="sl-grid">
              {filtered.map((item, index) => (
                <SubjectCard
                  key={item.subject_id || index}
                  item={item}
                  index={index}
                  busy={busy}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <MdlEditSubjectMasterList
        open={editOpen}
        subject_name={selectedSubject.name}
        subject_id={selectedSubject.id}
        session_id={sessionId}
        onClose={closeEdit}
      />
      <MdlAddSubjectMasterList
        open={addOpen}
        session_id={sessionId}
        onClose={closeAdd}
      />
      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={handleSelectedSession}
        />
      ) : null}
    </div>
  );
}
